/**
 * Centralized date formatting and parsing utilities.
 *
 * Display standard: DD.MM.YYYY (Bulgarian accounting convention)
 * Internal storage: YYYY-MM-DD (ISO 8601)
 */

/**
 * Parse a date input string in various formats and return a Date object.
 * Supports:
 *  - ISO:      YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD
 *  - European: DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY
 *  - Compact:  DDMMYYYY (8 digits, no separators)
 */
export function parseDateInput(value: string | null | undefined): Date | null {
  if (!value) return null

  const trimmed = value.trim()
  if (trimmed === "") return null

  // Compact 8-digit: DDMMYYYY
  const compactMatch = trimmed.match(/^(\d{2})(\d{2})(\d{4})$/)
  if (compactMatch) {
    const [, day, month, year] = compactMatch
    const d = new Date(Number(year), Number(month) - 1, Number(day))
    if (!Number.isNaN(d.getTime()) && d.getDate() === Number(day)) return d
    return null
  }

  // ISO: YYYY-MM-DD (with optional time)
  const isoMatch = trimmed.match(/^(\d{4})[-/.](\d{2})[-/.](\d{2})(?:$|[T\s])/)
  if (isoMatch) {
    const [, year, month, day] = isoMatch
    const d = new Date(Number(year), Number(month) - 1, Number(day))
    if (!Number.isNaN(d.getTime()) && d.getDate() === Number(day)) return d
    return null
  }

  // European: DD-MM-YYYY or DD/MM/YYYY or DD.MM.YYYY
  const euMatch = trimmed.match(/^(\d{2})[-/.](\d{2})[-/.](\d{4})$/)
  if (euMatch) {
    const [, day, month, year] = euMatch
    const d = new Date(Number(year), Number(month) - 1, Number(day))
    if (!Number.isNaN(d.getTime()) && d.getDate() === Number(day)) return d
    return null
  }

  // Fallback to native Date parsing (handles ISO timestamps, etc.)
  const parsed = new Date(trimmed)
  if (!Number.isNaN(parsed.getTime())) return parsed

  return null
}

/**
 * Convert any date input to ISO string (YYYY-MM-DD) for internal storage.
 */
export function toISODateString(value: string | Date | null | undefined): string | null {
  if (!value) return null

  const date = value instanceof Date ? value : parseDateInput(String(value))
  if (!date) return null

  const year = date.getFullYear().toString().padStart(4, "0")
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const day = date.getDate().toString().padStart(2, "0")
  return `${year}-${month}-${day}`
}

/**
 * Format a date for display: DD.MM.YYYY
 * This is the standard display format across the entire application.
 */
export function formatDisplayDate(value: string | Date | null | undefined): string {
  if (!value) return ""

  const date = value instanceof Date ? value : parseDateInput(String(value))
  if (!date) return String(value ?? "")

  const day = date.getDate().toString().padStart(2, "0")
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

/**
 * Format a date with time for display: DD.MM.YYYY HH:MM
 * Used for timestamps (uploaded_at, created_at, etc.)
 */
export function formatDisplayDateTime(value: string | Date | null | undefined): string {
  if (!value) return ""

  const date = value instanceof Date ? value : new Date(String(value))
  if (Number.isNaN(date.getTime())) return String(value ?? "")

  const day = date.getDate().toString().padStart(2, "0")
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const year = date.getFullYear()
  const hours = date.getHours().toString().padStart(2, "0")
  const minutes = date.getMinutes().toString().padStart(2, "0")
  return `${day}.${month}.${year} ${hours}:${minutes}`
}
